import { useState } from 'react';

import { IconEdit, IconPrinter } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { Modal } from '../../components/ui/Modal';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { openAuthenticatedRoute } from '../../lib/auth';
import { confirmUpdate } from '../../lib/confirm-service';
import { useTableSort } from '../../lib/table-sort';
import { useToast } from '../../lib/toast';
import { useInvestigationBillPatientBills, useUpdateInvestigationBillPayment } from './investigation-bill.queries';
import { currency, formatInvestigationDateTime } from './investigation-bill.utils';
import type { InvestigationBill, InvestigationPayment } from './types';

const paymentModes: InvestigationPayment['mode'][] = ['Cash', 'UPI', 'Transfer to Bank Account', 'Cheque', 'Other', 'Online'];

export const InvestigationBillAllReceiptList = ({ row }: { row: InvestigationBill }) => {
  const uhid = row.patientSnapshot.uhid;
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [editing, setEditing] = useState<{ billId: string; paymentIndex: number; payment: InvestigationPayment } | null>(null);
  const query = useInvestigationBillPatientBills(uhid, sortBy, sortOrder);
  const receipts = (query.data?.data ?? []).flatMap((bill) => bill.payments.map((payment, paymentIndex) => ({ key: `${bill.id}-${paymentIndex}`, bill, payment, paymentIndex })));
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
  });

  if (!uhid) return <EmptyState title="No UHID on file" message="This patient has no UHID assigned, so their receipt history can't be looked up." />;
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState title="Unable to load receipts" message={query.error instanceof ApiError ? query.error.message : 'Please try again.'} action={{ label: 'Try again', onClick: () => void query.refetch() }} />;
  const totalReceived = receipts.reduce((sum, receipt) => sum + receipt.payment.amount, 0);

  return (
    <>
      <header className="investigation-bill-detail-banner">
        <div className="investigation-bill-detail-banner-row"><span>All Receipt — {row.patientSnapshot.name}</span></div>
        <p>UHID {uhid} · {receipts.length} receipt{receipts.length === 1 ? '' : 's'}</p>
      </header>
      <section className="investigation-bill-detail-subsection">
        <div className="investigation-bill-detail-subsection-head"><h3>Receipts across all bills</h3><span>Total Received <strong>{currency(totalReceived)}</strong></span></div>
        {receipts.length === 0 ? (
          <p className="investigation-bill-detail-empty-note">No payments have been recorded for this patient.</p>
        ) : (
          <div className="table-scroll">
            <table className="investigation-bill-detail-payment-table">
              <thead><tr><th>#</th><SortableTh control={tableSort} column="billId">Bill No</SortableTh><SortableTh control={tableSort} column="billDate">Date</SortableTh><SortableTh control={tableSort} column="mode">Mode</SortableTh><SortableTh control={tableSort} column="bank">Bank</SortableTh><SortableTh control={tableSort} column="receivedBy">Received By</SortableTh><SortableTh control={tableSort} column="amount">Amount</SortableTh><th>Action</th></tr></thead>
              <tbody>
                {receipts.map((receipt, index) => (
                  <tr key={receipt.key}>
                    <td>{index + 1}</td>
                    <td>{receipt.bill.billing.billNumber}</td>
                    <td>{formatInvestigationDateTime(receipt.bill.billDate)}</td>
                    <td>{receipt.payment.mode}</td>
                    <td>{receipt.payment.bankName ?? '-'}</td>
                    <td>{receipt.bill.generatedBy.name}</td>
                    <td>{currency(receipt.payment.amount)}</td>
                    <td className="opd-row-action-group">
                      <button
                        type="button"
                        className="opd-row-action-icon"
                        title="Print Receipt"
                        aria-label="Print Receipt"
                        onClick={() => openAuthenticatedRoute(`/investigation-bill/${receipt.bill.id}/print-receipt/${receipt.paymentIndex}`)}
                      >
                        <IconPrinter />
                      </button>
                      <button
                        type="button"
                        className="opd-row-action-icon"
                        title="Edit Receipt"
                        aria-label="Edit Receipt"
                        onClick={() => setEditing({ billId: receipt.bill.id, paymentIndex: receipt.paymentIndex, payment: receipt.payment })}
                      >
                        <IconEdit />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <InvestigationBillEditReceiptModal editing={editing} onClose={() => setEditing(null)} />
    </>
  );
};

const InvestigationBillEditReceiptModal = ({ editing, onClose }: { editing: { billId: string; paymentIndex: number; payment: InvestigationPayment } | null; onClose: () => void }) => {
  const [mode, setMode] = useState<InvestigationPayment['mode']>('Cash');
  const [bankName, setBankName] = useState('');
  const [amount, setAmount] = useState(0);
  const [seed, setSeed] = useState(editing);
  if (editing !== seed) {
    setSeed(editing);
    if (editing) {
      setMode(editing.payment.mode);
      setBankName(editing.payment.bankName ?? '');
      setAmount(editing.payment.amount);
    }
  }
  const mutation = useUpdateInvestigationBillPayment();
  const { showToast } = useToast();

  const submit = () => {
    if (!editing || amount <= 0) return;
    void confirmUpdate('this receipt').then((confirmed) => {
      if (!confirmed) return;
      mutation.mutate(
        { id: editing.billId, paymentIndex: editing.paymentIndex, payload: { mode, bankName: bankName.trim() || null, amount } },
        {
          onSuccess: () => {
            showToast('Receipt saved successfully.', 'success');
            onClose();
          },
          onError: (error) => showToast(error instanceof ApiError ? error.message : 'The receipt could not be updated.', 'error'),
        },
      );
    });
  };

  return (
    <Modal open={editing !== null} onClose={() => { if (!mutation.isPending) onClose(); }} labelledBy="edit-investigation-receipt-title">
      <header>
        <div><p>Receipt</p><h2 id="edit-investigation-receipt-title">Edit Receipt</h2></div>
        <button type="button" onClick={onClose} disabled={mutation.isPending} aria-label="Close">x</button>
      </header>
      <form onSubmit={(event) => { event.preventDefault(); submit(); }}>
        <label className="field"><span>Mode</span><select value={mode} onChange={(event) => setMode(event.target.value as InvestigationPayment['mode'])}>{paymentModes.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label className="field"><span>Bank</span><input value={bankName} onChange={(event) => setBankName(event.target.value)} /></label>
        <label className="field"><span>Amount</span><input required type="number" min={0.01} step="0.01" value={amount} onChange={(event) => setAmount(Number(event.target.value))} /></label>
        <div className="bed-modal-actions">
          <button className="button button-secondary" type="button" onClick={onClose} disabled={mutation.isPending}>Cancel</button>
          <button className="button button-primary" type="submit" disabled={amount <= 0 || mutation.isPending}>{mutation.isPending ? 'Saving...' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
};
